import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

/**
 * <BackButton />
 * --------------------------------------------------------------
 * Premium reusable back navigation button. Calls `navigate(-1)`
 * when there's prior history, otherwise navigates to `fallback`
 * (default: "/"). i18n-aware label, premium black/gold styling.
 *
 * Props:
 *  - to: optional explicit destination; when provided, ALWAYS navigates there.
 *  - fallback: destination when no browser history is available (default "/").
 *  - label: custom label (defaults to `nav.back` / "Back").
 *  - className: extra classes to merge.
 *  - testId: override the data-testid (default "back-button").
 */
export default function BackButton({ to, fallback = "/", label, className = "", testId = "back-button" }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const onClick = () => {
    if (to) { navigate(to); return; }
    // If we have actual history, go back; otherwise route to fallback.
    if (window.history.length > 1 && window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors group ${className}`}
      data-testid={testId}
    >
      <span className="w-7 h-7 rounded-full border border-white/15 grid place-items-center group-hover:border-[#C8A24A]/60 group-hover:bg-[#C8A24A]/10 transition-colors">
        <ArrowLeft className="w-3.5 h-3.5" />
      </span>
      <span className="font-medium">{label || t("nav.back", "Back")}</span>
    </button>
  );
}
